import React, { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Tabs, Button, Typography, message } from 'antd';

import AdEditModal from './components/AdEditModal';
import ProjectDetails from './components/ProjectDetails';
import ProjectFormModal from './components/ProjectFormModal';
import ProjectsTable from './components/ProjectsTable';
import { AdProject, ProjectFormValues, AdAccount, GeneratedAd, SemanticUnit } from './types';
import MainLayout from '../../shared/common/components/layouts/MainLayout';

const { Title } = Typography;

// Моковые данные для демонстрации
const mockAdAccounts: AdAccount[] = [
  { id: '1', name: 'Основной кабинет', type: 'yandex_direct', isConnected: true },
  { id: '2', name: 'Второй кабинет', type: 'yandex_direct', isConnected: true },
  { id: '3', name: 'ВК Бизнес', type: 'vk', isConnected: true },
  { id: '4', name: 'Новый кабинет ВК', type: 'vk', isConnected: false },
];

const mockProjects: AdProject[] = [
  {
    id: '1',
    name: 'Курс по маркетингу',
    landingUrl: 'https://example.com/marketing',
    pageGoal: 'buy_course',
    paidProduct: {
      name: 'Курс «Основы маркетинга»',
      keyIdea: 'Научим привлекать клиентов без бюджета за 30 дней с помощью проверенных стратегий',
      averageCheck: 15000,
      salesPageUrl: 'https://example.com/sales',
    },
    companyInfo: {
      companyName: 'ООО Маркетинг Про',
      companyActivity: 'Обучение маркетингу и продвижению бизнеса в интернете',
      authorName: 'Иван Иванов',
      authorDescription: 'Эксперт с 10-летним опытом, автор 3 книг по маркетингу',
    },
    adAccountIds: ['1', '3'],
    settings: {
      excludeWords: ['бесплатно', 'халява'],
      keywords: ['маркетинг', 'продвижение', 'клиенты'],
    },
    notes: 'Основной курс компании',
    mode: 'manual',
    lifeSpheres: [
      {
        id: 'sf1',
        name: 'Эмоциональное состояние',
        code: 'СФ',
        themes: [
          {
            id: 'tm1',
            name: 'Хроническая тревожность',
            relevance: 8.9,
            semanticUnits: [
              {
                id: 'su1',
                title: 'Тревога как фоновое состояние',
                description: 'Тревога есть постоянно, даже когда внешне всё спокойно. Не могу расслабиться и насладиться моментом.',
                weight: 63,
              },
              {
                id: 'su2',
                title: 'Невозможность расслабиться',
                description: 'Я не могу по-настоящему расслабиться даже дома и в безопасности. Постоянное напряжение.',
                weight: 63,
              },
              {
                id: 'su3',
                title: 'Голова не выключается',
                description: 'Мысли не останавливаются, напряжение в голове не отпускает. Бесконечный внутренний диалог.',
                weight: 63,
              },
            ],
          },
          {
            id: 'tm2',
            name: 'Страхи (за будущее, деньги, здоровье)',
            relevance: 8.7,
            semanticUnits: [
              {
                id: 'su4',
                title: 'Постоянное ожидание плохого',
                description: 'Я всё время жду, что случится что-то плохое. Не могу радоваться настоящему.',
                weight: 58,
              },
              {
                id: 'su5',
                title: 'Тревога за стабильность',
                description: 'Нет ощущения опоры — будущее, деньги и здоровье тревожат. Нет уверенности в завтрашнем дне.',
                weight: 55,
              },
            ],
          },
        ],
      },
      {
        id: 'sf2',
        name: 'Финансы и карьера',
        code: 'СФ',
        themes: [
          {
            id: 'tm3',
            name: 'Нехватка денег',
            relevance: 8.5,
            semanticUnits: [
              {
                id: 'su6',
                title: 'Денег всегда не хватает',
                description: 'Сколько бы ни зарабатывал, денег всегда не хватает. Постоянная финансовая тревога.',
                weight: 70,
              },
              {
                id: 'su7',
                title: 'Страх остаться без денег',
                description: 'Боюсь потерять источник дохода и остаться ни с чем. Это парализует.',
                weight: 65,
              },
            ],
          },
        ],
      },
    ],
    generatedAds: [
      {
        id: 'ad1',
        semanticUnitId: 'su1',
        platform: 'yandex_direct',
        headline: 'Устали от постоянной тревоги?',
        subheadline: 'Научитесь управлять стрессом за 30 дней',
        text: 'Курс «Основы маркетинга» поможет вам найти внутреннее спокойствие и уверенность в бизнесе. Проверенные техники от эксперта с 10-летним опытом.',
        imageUrl: '',
        imageText: 'Спокойствие и уверенность',
        quickLinks: [
          { title: 'О курсе', url: 'https://example.com/about' },
          { title: 'Отзывы', url: 'https://example.com/reviews' },
        ],
        clarifications: ['Гарантия результата', 'Поддержка куратора', 'Доступ навсегда'],
        status: 'draft',
      },
      {
        id: 'ad2',
        semanticUnitId: 'su6',
        platform: 'vk',
        headline: 'Денег всегда не хватает?',
        subheadline: 'Измените это за 30 дней',
        text: 'Научитесь привлекать клиентов и увеличьте доход. Курс от практика с 10-летним опытом в маркетинге.',
        imageUrl: '',
        imageText: 'Финансовая свобода',
        quickLinks: [],
        clarifications: ['Практические задания', 'Обратная связь'],
        status: 'active',
      },
    ],
    status: 'ready',
    createdAt: '2024-01-15',
    updatedAt: '2024-01-15',
  },
  {
    id: '2',
    name: 'Вебинар по продажам',
    landingUrl: 'https://example.com/webinar',
    pageGoal: 'webinar_registration',
    paidProduct: {
      name: 'Интенсив «Продажи без возражений»',
      keyIdea: 'Научитесь закрывать сделки без давления на клиента',
      averageCheck: 5000,
      salesPageUrl: '',
    },
    companyInfo: {
      companyName: '',
      companyActivity: '',
      authorName: 'Мария Петрова',
      authorDescription: 'Тренер по продажам, 500+ учеников',
    },
    adAccountIds: ['1'],
    settings: {
      excludeWords: [],
      keywords: ['продажи', 'переговоры'],
    },
    notes: '',
    mode: 'auto',
    lifeSpheres: [],
    generatedAds: [],
    status: 'draft',
    createdAt: '2024-01-20',
    updatedAt: '2024-01-20',
  },
];

const Aps: React.FC = () => {
  const [projects, setProjects] = useState<AdProject[]>(mockProjects);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<AdProject | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewingProject, setViewingProject] = useState<AdProject | null>(null);
  const [editingAd, setEditingAd] = useState<GeneratedAd | null>(null);
  const [adModalVisible, setAdModalVisible] = useState(false);

  const handleCreateProject = () => {
    setEditingProject(null);
    setModalVisible(true);
  };

  const handleEditProject = (project: AdProject) => {
    setEditingProject(project);
    setModalVisible(true);
  };

  const handleViewProject = (project: AdProject) => {
    setViewingProject(project);
  };

  const handleBackToList = () => {
    setViewingProject(null);
  };

  const handleDeleteProject = (id: string) => {
    setProjects(prev => prev.filter(p => p.id !== id));
    message.success('Проект удалён');
  };

  const handleToggleStatus = (project: AdProject) => {
    const newStatus = project.status === 'active' ? 'paused' : 'active';
    setProjects(prev =>
      prev.map(p =>
        p.id === project.id ? { ...p, status: newStatus } : p
      )
    );
    if (viewingProject?.id === project.id) {
      setViewingProject(prev => prev ? { ...prev, status: newStatus } : null);
    }
    message.success(
      newStatus === 'active' ? 'Проект запущен' : 'Проект приостановлен'
    );
  };

  const handleStartAnalysis = () => {
    if (!viewingProject) return;
    
    // Имитация запуска анализа
    setProjects(prev =>
      prev.map(p =>
        p.id === viewingProject.id ? { ...p, status: 'analyzing' } : p
      )
    );
    setViewingProject(prev => prev ? { ...prev, status: 'analyzing' } : null);
    
    message.info('Анализ запущен...');
    
    // Имитация завершения анализа через 3 секунды
    setTimeout(() => {
      const updatedProject = {
        ...viewingProject,
        status: 'ready' as const,
        lifeSpheres: mockProjects[0].lifeSpheres, // Используем моковые данные
      };
      setProjects(prev =>
        prev.map(p => p.id === viewingProject.id ? updatedProject : p)
      );
      setViewingProject(updatedProject);
      message.success('Анализ завершён! Карта смыслов создана.');
    }, 3000);
  };

  const handleGenerateAds = () => {
    message.info('Генерация объявлений...');
    // TODO: Реализовать генерацию объявлений через API
  };

  const handleEditAd = (ad: GeneratedAd) => {
    setEditingAd(ad);
    setAdModalVisible(true);
  };

  const handleAdModalCancel = () => {
    setAdModalVisible(false);
    setEditingAd(null);
  };

  const handleAdModalSubmit = (updatedAd: GeneratedAd) => {
    if (!viewingProject) return;

    const updatedProject = {
      ...viewingProject,
      generatedAds: viewingProject.generatedAds.map(a =>
        a.id === updatedAd.id ? updatedAd : a
      ),
    };

    setProjects(prev =>
      prev.map(p => p.id === viewingProject.id ? updatedProject : p)
    );
    setViewingProject(updatedProject);
    setAdModalVisible(false);
    setEditingAd(null);
    message.success('Объявление обновлено');
  };

  const handleSendAd = (ad: GeneratedAd) => {
    if (!viewingProject) return;

    const updatedAd = { ...ad, status: 'sent' as const };
    const updatedProject = {
      ...viewingProject,
      generatedAds: viewingProject.generatedAds.map(a =>
        a.id === ad.id ? updatedAd : a
      ),
    };

    setProjects(prev =>
      prev.map(p => p.id === viewingProject.id ? updatedProject : p)
    );
    setViewingProject(updatedProject);
    message.success(`Объявление отправлено: ${ad.headline}`);
  };

  // Собираем все смысловые единицы для модалки редактирования
  const allSemanticUnits: SemanticUnit[] = viewingProject?.lifeSpheres.flatMap(sphere =>
    sphere.themes.flatMap(theme => theme.semanticUnits)
  ) || [];

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingProject(null);
  };

  const handleModalSubmit = (values: ProjectFormValues) => {
    setModalLoading(true);

    setTimeout(() => {
      if (editingProject) {
        const updatedProject = {
          ...editingProject,
          name: values.name,
          landingUrl: values.landingUrl,
          pageGoal: values.pageGoal,
          paidProduct: {
            name: values.productName,
            keyIdea: values.productKeyIdea,
            averageCheck: values.productAverageCheck,
            salesPageUrl: values.productSalesPageUrl,
          },
          companyInfo: {
            companyName: values.companyName,
            companyActivity: values.companyActivity,
            authorName: values.authorName,
            authorDescription: values.authorDescription,
          },
          adAccountIds: values.adAccountIds || [],
          settings: {
            excludeWords: values.excludeWords?.split(',').map(s => s.trim()).filter(Boolean) || [],
            keywords: values.keywords?.split(',').map(s => s.trim()).filter(Boolean) || [],
          },
          notes: values.notes || '',
          mode: values.mode,
          updatedAt: new Date().toISOString(),
        };
        
        setProjects(prev =>
          prev.map(p => p.id === editingProject.id ? updatedProject : p)
        );
        
        if (viewingProject?.id === editingProject.id) {
          setViewingProject(updatedProject);
        }
        
        message.success('Проект обновлён');
      } else {
        const newProject: AdProject = {
          id: Date.now().toString(),
          name: values.name,
          landingUrl: values.landingUrl,
          pageGoal: values.pageGoal,
          paidProduct: {
            name: values.productName,
            keyIdea: values.productKeyIdea,
            averageCheck: values.productAverageCheck,
            salesPageUrl: values.productSalesPageUrl,
          },
          companyInfo: {
            companyName: values.companyName,
            companyActivity: values.companyActivity,
            authorName: values.authorName,
            authorDescription: values.authorDescription,
          },
          adAccountIds: values.adAccountIds || [],
          settings: {
            excludeWords: values.excludeWords?.split(',').map(s => s.trim()).filter(Boolean) || [],
            keywords: values.keywords?.split(',').map(s => s.trim()).filter(Boolean) || [],
          },
          notes: values.notes || '',
          mode: values.mode,
          lifeSpheres: [],
          generatedAds: [],
          status: 'draft',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setProjects(prev => [newProject, ...prev]);
        message.success('Проект создан');
      }

      setModalLoading(false);
      setModalVisible(false);
      setEditingProject(null);
    }, 500);
  };

  const projectsTab = (
    <div>
      {viewingProject ? (
        <ProjectDetails
          project={viewingProject}
          onBack={handleBackToList}
          onEdit={() => handleEditProject(viewingProject)}
          onToggleStatus={() => handleToggleStatus(viewingProject)}
          onStartAnalysis={handleStartAnalysis}
          onGenerateAds={handleGenerateAds}
          onEditAd={handleEditAd}
          onSendAd={handleSendAd}
        />
      ) : (
        <>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateProject}
            >
              Создать проект
            </Button>
          </div>
          <ProjectsTable
            projects={projects}
            loading={loading}
            onEdit={handleEditProject}
            onDelete={handleDeleteProject}
            onView={handleViewProject}
            onToggleStatus={handleToggleStatus}
          />
        </>
      )}
    </div>
  );

  const lifeSpheresTab = (
    <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
      <Title level={4} type="secondary">Сферы жизни</Title>
      <p>Раздел в разработке</p>
    </div>
  );

  const tabs = [
    {
      label: 'Рекламные проекты',
      key: 'ad-projects',
      children: projectsTab,
    },
    {
      label: 'Сферы жизни',
      key: 'life-spheres',
      children: lifeSpheresTab,
    },
  ];

  return (
    <MainLayout pageTitle="Центр управления рекламой">
      <div className="page-wrapper">
        <div className="page-content">
          <Title level={2}>Центр управления рекламой</Title>
          <Tabs type="card" items={tabs} />
        </div>
      </div>

      <ProjectFormModal
        visible={modalVisible}
        project={editingProject}
        adAccounts={mockAdAccounts}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
        loading={modalLoading}
      />

      <AdEditModal
        visible={adModalVisible}
        ad={editingAd}
        semanticUnits={allSemanticUnits}
        onCancel={handleAdModalCancel}
        onSubmit={handleAdModalSubmit}
      />
    </MainLayout>
  );
};

export default Aps;
